import "../styles/profile.css";
import buildhubLogo from "../images/bg.png";
import profileFiller from "../images/empty.jpg";
import { useState, useEffect, useContext } from "react";
import { ProfileContext } from "../context/contexts";
import ChangePasswordModal from "./change-password-modal";

function MainProfile() {
  const profileDetail = useContext(ProfileContext);

  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageData, setImageData] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  useEffect(() => {
    let userProfilePhoto = "";
    if (profileDetail?.userDetailsApi?.profile_photo) {
      userProfilePhoto = `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/${profileDetail.userDetailsApi.profile_photo}`;
    } else {
      userProfilePhoto = profileFiller;
    }
    setImageData(userProfilePhoto);
  }, [profileDetail]);

  useEffect(() => {
    if (!image) {
      setImagePreview(imageData);
      return;
    }

    const objectUrl = URL.createObjectURL(image);
    setImagePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [image, imageData]);

  const userDetails = profileDetail?.userDetailsApi;
  const displayName = profileDetail?.userDisplayName || "";
  const initialDisplayName = userDetails?.display_name || "";
  const hasImageUpdate = Boolean(image);
  const hasDisplayNameUpdate = displayName !== initialDisplayName;
  const hasPendingChanges = hasImageUpdate || hasDisplayNameUpdate;

  const toggleChangePasswordModal = () => {
    setShowChangePasswordModal((prev) => !prev);
  };

  const handleImageChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.type.startsWith("image/")) {
      setUploadError("Please select a valid image file.");
      return;
    }

    setUploadError("");
    setImage(selectedFile);
    setImageData(selectedFile);
  };

  const handleCancelChanges = () => {
    setImage(null);
    setImageData(
      userDetails?.profile_photo
        ? `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/${userDetails.profile_photo}`
        : profileFiller
    );
    profileDetail?.handleDisplayNameChange(initialDisplayName);
    setUploadError("");
  };

  const handleSubmit = async () => {
    if (!hasPendingChanges || isSaving) return;

    setIsSaving(true);
    const didSave = await profileDetail.handleSubmitChanges(imageData);

    if (!didSave) {
      setIsSaving(false);
    }
  };

  return (
    <div className='profile-main'>
      <div className='profile-page-glow' style={{ backgroundImage: `url(${buildhubLogo})` }} />

      <div className='profile-shell'>
        <header className='profile-header-card'>
          <a href='/' className='profile-back-link'>
            <i className='fa fa-arrow-left' aria-hidden='true'></i>
            <span>Back to Chat</span>
          </a>

          <div className='profile-identity'>
            <img src={imagePreview || profileFiller} alt='Profile avatar' className='profile-avatar-large' />
            <div>
              <h1>{displayName || "My Profile"}</h1>
              <p>{userDetails?.email_address || ""}</p>
            </div>
          </div>

          <div className='profile-header-status'>
            <span className='profile-status-pill'>Account Active</span>
            <span className='profile-subtle-caption'>Manage your identity and account settings.</span>
          </div>
        </header>

        <div className='profile-content-grid'>
          <aside className='profile-side-card'>
            <h2>Photo</h2>
            <p className='profile-card-description'>Upload a clear headshot to help teammates recognize you quickly.</p>

            <img src={imagePreview || profileFiller} alt='Profile preview' className='profile-avatar-preview' />

            <label htmlFor='profile-image-upload' className='profile-upload-btn'>
              <i className='fa fa-upload' aria-hidden='true'></i>
              <span>Upload New Photo</span>
            </label>
            <input id='profile-image-upload' type='file' accept='image/*' onChange={handleImageChange} />

            {image && <p className='profile-file-feedback'>{image.name}</p>}
            {uploadError && <p className='profile-error-feedback'>{uploadError}</p>}

            <button type='button' className='profile-secondary-btn' onClick={toggleChangePasswordModal}>
              Change Password
            </button>
          </aside>

          <main className='profile-main-card'>
            <section className='profile-section'>
              <div className='profile-section-heading'>
                <h2>Personal Information</h2>
                <p>Update the details visible across your chat workspace.</p>
              </div>

              <div className='profile-field-grid'>
                <div className='profile-field'>
                  <label htmlFor='display_name'>Display Name</label>
                  <input
                    type='text'
                    id='display_name'
                    className='profile-text-input'
                    value={displayName}
                    onChange={(e) => profileDetail.handleDisplayNameChange(e.target.value)}
                  />
                </div>
              </div>
            </section>

            <section className='profile-section'>
              <div className='profile-section-heading'>
                <h2>Account Information</h2>
                <p>These fields are managed centrally by your organization.</p>
              </div>

              <div className='profile-field-grid'>
                <div className='profile-field'>
                  <label htmlFor='profile-id'>User ID</label>
                  <input id='profile-id' type='text' className='profile-text-input' value={userDetails?.id || ""} disabled />
                </div>
                <div className='profile-field'>
                  <label htmlFor='first_name'>First Name</label>
                  <input
                    id='first_name'
                    type='text'
                    className='profile-text-input'
                    value={userDetails?.first_name || ""}
                    disabled
                  />
                </div>
                <div className='profile-field'>
                  <label htmlFor='last_name'>Last Name</label>
                  <input
                    id='last_name'
                    type='text'
                    className='profile-text-input'
                    value={userDetails?.last_name || ""}
                    disabled
                  />
                </div>
                <div className='profile-field'>
                  <label htmlFor='email_address'>Email Address</label>
                  <input
                    id='email_address'
                    type='text'
                    className='profile-text-input'
                    value={userDetails?.email_address || ""}
                    disabled
                  />
                </div>
                <div className='profile-field'>
                  <label htmlFor='department'>Department</label>
                  <input
                    id='department'
                    type='text'
                    className='profile-text-input'
                    value={userDetails?.department || ""}
                    disabled
                  />
                </div>
                <div className='profile-field'>
                  <label htmlFor='username'>Username</label>
                  <input
                    id='username'
                    type='text'
                    className='profile-text-input'
                    value={userDetails?.username || ""}
                    disabled
                  />
                </div>
                <div className='profile-field'>
                  <label htmlFor='admin_access'>Admin Access</label>
                  <input
                    id='admin_access'
                    type='text'
                    className='profile-text-input'
                    value={userDetails?.admin_access || ""}
                    disabled
                  />
                </div>
              </div>

              <p className='profile-card-description'>Need to change locked fields? Contact support for identity updates.</p>
            </section>

            <section className='profile-action-bar'>
              <button type='button' className='profile-ghost-btn' onClick={handleCancelChanges} disabled={!hasPendingChanges || isSaving}>
                Cancel
              </button>
              <button
                className='profile-submit-btn'
                type='button'
                onClick={handleSubmit}
                disabled={!hasPendingChanges || isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </section>
          </main>
        </div>
      </div>

      {showChangePasswordModal && <ChangePasswordModal showHideChangePasswordModal={toggleChangePasswordModal} />}
    </div>
  );
}

export default MainProfile;
